import React, { useState, useEffect } from 'react';
import { Tabs, Button, Card, Row } from 'antd';
import { useLocation } from 'react-router-dom';
import ProposalCompanySelect from '../../components/Proposal/ProposalCompanySelect';
import ControlAddress from '../../components/Companies/ControlAddress';
import Parametres from '../../components/Proposal/Parametres';
import NoData from '../../assets/nodata.png';
import ProposalService from '../../services/ProposalService';
import AuthorizedPersonTable from '../../components/Companies/AuthorizedPersonTable';
import { hasRole, openNotification } from 'utils';
import {
    resolvePreviousProposalTotalPrice,
    shouldUseProposalReportAttributes,
} from '../../components/Proposal/proposalRenewalInfo';
import {
    areProposalRenewalSourcesCompatible,
    mergeAuthorizedPersonIds,
    mergeRenewalProposalAttributes,
    parseRenewalProposalIds,
} from '../../utils/proposalRenewalMulti';

const { TabPane } = Tabs;

type PreviousProposalInfo = {
    id: number;
    proposalNumber?: string | null;
    createdAt?: string | null;
    totalPrice?: number | null;
};

const Proposals: React.FC = () => {
    const location = useLocation();
    const [activeKey, setActiveKey] = useState('1');
    const [companyId, setCompanyId] = useState<any>(null);
    const [attributes, setAttributes] = useState<any>(null);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [selectedAddressId, setSelectedAddressId] = useState<any>(null);
    const [roles, setRoles] = useState<any>();
    const [selectedAuthorizedPersonIds, setSelectedAuthorizedPersonIds] = useState<number[]>([]); // Multiple selected authorized person IDs
    const [renewal, setRenewal] = useState(true);
    const [previousProposalTotalPrice, setPreviousProposalTotalPrice] = useState<number | null>(null);
    const [previousProposalNumber, setPreviousProposalNumber] = useState<string | null>(null);
    const [previousProposalDate, setPreviousProposalDate] = useState<string | null>(null);
    const [previousProposals, setPreviousProposals] = useState<PreviousProposalInfo[]>([]);
    const isPartner = localStorage.getItem('isPartner');
    const [isAdmin, setIsAdmin] = useState(false)
    const [isCentralUser, setIsCentralUser] = useState(false);
    const [isCustomer, setIsCustomer] = useState(false);

    // Function to extract query params
    const getQueryParams = (search: string) => {
        return new URLSearchParams(search);
    };
    const queryParams = getQueryParams(location.search);
    const isRenewMode = queryParams.get('mode') === 'renew';
    const renewalSource = queryParams.get('source');

    useEffect(() => {
        let storedRoles: any = localStorage.getItem('roles');

        if(storedRoles.indexOf("ADMIN") > -1){
            setIsAdmin(true);
        }

                const isUserCentralUser = hasRole('CENTRAL_USER');
                setIsCentralUser(isUserCentralUser);

                const isUserCustomer = hasRole('CUSTOMER');
                setIsCustomer(isUserCustomer);

        let parsedRoles = JSON.parse(storedRoles);
        setRoles(parsedRoles);

        const queryParams = getQueryParams(location.search);
        const companyIdFromQuery = queryParams.get('companyId');
        const proposalIdsFromQuery = parseRenewalProposalIds(location.search);
        const isEditQuery: any = queryParams.get('isEdit');
        const activeKey: any = queryParams.get('activeKey');
        if (companyIdFromQuery) {
            setActiveKey('2');
            setCompanyId(companyIdFromQuery);
        }
        if (activeKey) {
            setActiveKey('2');
        }
        if (proposalIdsFromQuery.length === 0) {
            setRenewal(true);
            setPreviousProposalTotalPrice(null);
            setPreviousProposalNumber(null);
            setPreviousProposalDate(null);
            setPreviousProposals([]);
        }
        if (proposalIdsFromQuery.length > 0) {
            setActiveKey('2');
            const getInfos = async () => {
                try {
                    const proposalResponses: any[] = await Promise.all(
                        proposalIdsFromQuery.map((proposalId) => ProposalService.get(proposalId))
                    );
                    const proposals = proposalResponses.map((response) => response?.data);
                    const firstProposal = proposals[0];

                    if (isRenewMode && !areProposalRenewalSourcesCompatible(proposals)) {
                        setCompanyId(null);
                        setSelectedAddressId(null);
                        setAttributes(null);
                        setSelectedAuthorizedPersonIds([]);
                        setPreviousProposalTotalPrice(null);
                        setPreviousProposalNumber(null);
                        setPreviousProposalDate(null);
                        setPreviousProposals([]);
                        openNotification('error', 'Hata', 'Çoklu teklif oluşturmak için aynı firma ve aynı lokasyondaki teklifler seçilmelidir.');
                        return;
                    }

                    const attributesResponses = shouldUseProposalReportAttributes(renewalSource)
                        ? await Promise.all(proposalIdsFromQuery.map((proposalId) => ProposalService.getReportDetails(proposalId)))
                        : proposalResponses;
                    const mergedAttributes = mergeRenewalProposalAttributes(
                        attributesResponses.map((response) => response?.data?.attributes ?? [])
                    );
                    const previousProposalInfos: PreviousProposalInfo[] = isRenewMode
                        ? proposals.map((proposal, index) => ({
                            id: proposalIdsFromQuery[index],
                            proposalNumber: proposal?.proposalNumber ?? null,
                            createdAt: proposal?.createdAt ?? null,
                            totalPrice: resolvePreviousProposalTotalPrice(proposal, renewalSource),
                        }))
                        : [];
                    const previousTotalPrice = previousProposalInfos.reduce(
                        (total, proposal) => total + (proposal.totalPrice ?? 0),
                        0
                    );
                    const hasPreviousTotalPrice = previousProposalInfos.some((proposal) => proposal.totalPrice !== null);

                    setCompanyId(firstProposal?.company?.id);
                    setSelectedAddressId(firstProposal?.address?.id);
                    setAttributes(mergedAttributes);
                    // Yenileme bağlantıları kaynak teklifin yenileme ayarını devralmaz.
                    setRenewal(isRenewMode ? true : firstProposal?.renewal ?? true);
                    setPreviousProposals(previousProposalInfos);
                    setPreviousProposalTotalPrice(
                        isRenewMode && hasPreviousTotalPrice ? previousTotalPrice : null
                    );
                    setPreviousProposalNumber(
                        isRenewMode ? previousProposalInfos.map((proposal) => proposal.proposalNumber).filter(Boolean).join(', ') || null : null
                    );
                    setPreviousProposalDate(
                        isRenewMode && previousProposalInfos.length === 1 ? previousProposalInfos[0].createdAt ?? null : null
                    );
                    setSelectedAuthorizedPersonIds(
                        mergeAuthorizedPersonIds(proposals.map((proposal) => proposal?.authorizedPersonIds))
                    );
                    if (isEditQuery && isEditQuery === 'true') {
                        setIsEdit(true);
                    }
                } catch (error) {
                    console.error('Teklif bilgileri yüklenirken hata oluştu:', error);
                    openNotification('error', 'Hata', 'Teklif bilgileri yüklenirken bir hata oluştu.');
                }
            };
            getInfos();
        }
    }, [location.search, isRenewMode, renewalSource]);

    return (isAdmin === true || isCentralUser === true || isCustomer === true || isPartner?.toString() === 'true') ? <div style={{ paddingBottom: 120 }}>
            <ProposalCompanySelect setCompanyId={setCompanyId} companyId={companyId} />

            {/* Kontrol Adresi Kartı */}
            <Card className='br-30px mt-16px'>
                {companyId ? (
                    <ControlAddress
                        companyId={companyId}
                        type={true}
                        setSelectedAddress={setSelectedAddressId}
                        selectedAddressId={selectedAddressId}
                        isEdit={isEdit}
                    />
                ) : (
                    <div>
                        <Row justify={'space-between'} align={'middle'}>
                            <h3>Kontrol Adresi</h3>
                            <Button type='primary' className='d-button dark-button width-auto' disabled>
                                Yeni Kontrol Adresi Ekle
                            </Button>
                        </Row>
                        <Row justify={'center'}>
                            <img src={NoData} style={{ height: '50px' }} />
                        </Row>
                        <Row justify={'center'}>
                            <p>Teklif oluşturmak için firma seçmelisiniz.</p>
                        </Row>
                    </div>
                )}
            </Card>

            {/* Yetkili Kişi Tablosu */}
            <Card className='br-30px mt-16px'>
                {companyId ? (
                    <>
                        <AuthorizedPersonTable
                            type={true}
                            companyId={companyId}
                            controlAddressId={selectedAddressId}
                            selectionFilter='offerOnly'
                            setSelectedAuthorizedPersonIds={setSelectedAuthorizedPersonIds} // Set multiple IDs
                            selectedAuthorizedPersonIds={selectedAuthorizedPersonIds} // Pass multiple IDs
                        />
                    </>
                ) : (
                    <div>
                        <Row justify={'space-between'} align={'middle'}>
                            <h3>Yetkili Kişi</h3>
                            <Button type='primary' className='d-button dark-button width-auto' disabled>
                                Yeni Yetkili Ekle
                            </Button>
                        </Row>
                        <Row justify={'center'}>
                            <img src={NoData} style={{ height: '50px' }} />
                        </Row>
                        <Row justify={'center'}>
                            <p>Teklif oluşturmak için firma seçmelisiniz.</p>
                        </Row>
                    </div>
                )}
            </Card>

            {/* Parametre Kartı */}
            <Card className='br-30px mt-16px'>
                {companyId ? (
                    <Parametres
                        companyId={companyId}
                        addressId={selectedAddressId}
                        attributes={attributes}
                        isEdit={isEdit}
                        authorizedPersonIds={selectedAuthorizedPersonIds} // Pass multiple IDs to Parametres
                        initialRenewal={renewal}
                        isRenewMode={isRenewMode}
                        previousProposalTotalPrice={previousProposalTotalPrice}
                        previousProposalNumber={previousProposalNumber}
                        previousProposalDate={previousProposalDate}
                        previousProposals={previousProposals}
                    />
                ) : (
                    <div>
                        <Row>
                            <h3>Parametre Listesi</h3>
                        </Row>
                        <Row justify={'center'}>
                            <img src={NoData} style={{ height: '50px' }} />
                        </Row>
                        <Row justify={'center'}>
                            <p>Teklif oluşturmak için firma seçmelisiniz.</p>
                        </Row>
                    </div>
                )}
            </Card>
        </div>
    : <h3 className="text-center">Teklif oluşturabilmek için profil sayfanızda yer alan Sözleşme bölümündeki İş Ortaklığı Sözleşmesini onaylamanız gerekmektedir.</h3>;
};

export default Proposals;
